import { useCallback, useEffect, useState } from "react";
import { api, apiErrorMessage } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BarChart3, TrendingUp, Trophy } from "lucide-react";
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { toast } from "sonner";

export function PartnerAnalyticsCard() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get("/perk-partners/me/analytics", { params: { days } });
      setData(d);
    } catch (e) { toast.error(apiErrorMessage(e) || "Failed to load analytics"); }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  if (!data) return null;

  const maxClaim = Math.max(1, ...(data.top_perks || []).map((p) => p.claim_count || 0));

  return (
    <Card className="p-5 border-white/10 bg-card mb-4" data-testid="partner-analytics-card">
      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <BarChart3 className="h-4 w-4 text-orange-400" />
          <div>
            <div className="text-[11px] uppercase tracking-widest text-zinc-500">Analytics</div>
            <div className="text-sm font-semibold font-display">Perk performance</div>
          </div>
        </div>
        <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
          <SelectTrigger className="w-32 h-8 bg-white/[0.04] border-white/10"
                          data-testid="partner-analytics-range">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-[#0a0a0b] border-white/10 text-white">
            <SelectItem value="7">Last 7 days</SelectItem>
            <SelectItem value="30">Last 30 days</SelectItem>
            <SelectItem value="90">Last 90 days</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-3">
        <div className="rounded-md border border-white/5 bg-white/[0.02] px-3 py-2"
             data-testid="partner-analytics-window">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">Claims · {days}d</div>
          <div className="text-lg font-semibold tabular-nums text-orange-300">
            {data.total_claims_window}
          </div>
        </div>
        <div className="rounded-md border border-white/5 bg-white/[0.02] px-3 py-2"
             data-testid="partner-analytics-total">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">All-time claims</div>
          <div className="text-lg font-semibold tabular-nums text-zinc-100">
            {data.total_claims_all_time}
          </div>
        </div>
        <div className="rounded-md border border-white/5 bg-white/[0.02] px-3 py-2"
             data-testid="partner-analytics-perks">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">Live perks</div>
          <div className="text-lg font-semibold tabular-nums text-zinc-100">
            {data.active_perks}
            <span className="text-xs text-zinc-500 ml-1">/ {data.perks_count}</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-3">
        <div className="rounded-md border border-white/5 p-3">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-1 mb-1">
            <TrendingUp className="h-3 w-3" /> Claims per day
          </div>
          <div style={{ width: "100%", height: 160 }} data-testid="partner-analytics-chart">
            <ResponsiveContainer minHeight={160}>
              <LineChart data={data.claims_by_day} margin={{ top: 6, right: 6, bottom: 0, left: -18 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                <XAxis dataKey="date" tick={{ fill: "#a1a1aa", fontSize: 9 }}
                       tickFormatter={(v) => (v || "").slice(5)}
                       interval="preserveStartEnd" />
                <YAxis allowDecimals={false} tick={{ fill: "#a1a1aa", fontSize: 10 }} width={26} />
                <Tooltip contentStyle={{
                  background: "#0a0a0b",
                  border: "1px solid rgba(255,255,255,0.1)",
                  fontSize: 12,
                }} />
                <Line type="monotone" dataKey="count" stroke="#f97316"
                      strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="rounded-md border border-white/5 p-3"
             data-testid="partner-analytics-leaderboard">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-1 mb-2">
            <Trophy className="h-3 w-3" /> Top perks
          </div>
          {(data.top_perks || []).length === 0 ? (
            <div className="text-[11px] text-zinc-500 italic" data-testid="partner-analytics-empty">
              No perks yet — publish your first offer to start collecting claims.
            </div>
          ) : (
            <div className="space-y-2">
              {data.top_perks.map((p, i) => (
                <div key={p.id} data-testid={`partner-analytics-perk-${p.id}`}>
                  <div className="flex items-center justify-between text-xs gap-2">
                    <span className="text-zinc-200 truncate min-w-0">
                      <span className="text-zinc-500 mr-1">#{i + 1}</span>{p.title}
                    </span>
                    <span className="tabular-nums text-orange-300 shrink-0">
                      {p.claim_count}
                    </span>
                  </div>
                  <div className="mt-1 h-1.5 rounded-full bg-white/5 overflow-hidden">
                    <div className="h-full bg-orange-500/70"
                         style={{ width: `${Math.max(4, ((p.claim_count || 0) / maxClaim) * 100)}%` }} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}

export default PartnerAnalyticsCard;
